import { FC, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Divider,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
} from '@features/ui';
import {
  payoutRequestSchema,
  LeelooAccounts,
  PayoutRequestFormData,
  UserProfile,
} from '@src/types/users.ts';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { formatCurrency, formatDate } from '@lib/utils.ts';
import { getPayoutManualInfo } from '@src/services/payout.ts';

interface PayoutSummaryInfo {
  totalPayouts?: number | null;
  totalPaid?: number | string | null;
  lastPayoutOn?: string | null;
  minPayout?: number | null;
  maxPayout?: number | null;
}

interface AddPayoutRequestPopupProps {
  open: boolean;
  user: UserProfile | null;
  account: LeelooAccounts | null;
  onOpenChange: (open: boolean) => void;
  onSave: (data: PayoutRequestFormData) => void;
  payoutSummary?: PayoutSummaryInfo;
}
export const AddPayoutRequestPopup: FC<AddPayoutRequestPopupProps> = ({
  open,
  user,
  account,
  onOpenChange,
  onSave,
  payoutSummary,
}) => {
  const [fetchedPayoutSummary, setFetchedPayoutSummary] =
    useState<PayoutSummaryInfo | null>(null);
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<PayoutRequestFormData>({
    resolver: yupResolver(payoutRequestSchema),
  });

  useEffect(() => {
    if (open) {
      reset({
        rithmic_deduction_amount: undefined,
        payout_amount: undefined,
        override: undefined,
        note: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const fetchPayoutSummary = async () => {
      if (open && account?.id) {
        try {
          const response = await getPayoutManualInfo(account.id);
          const data = response.data;

          setFetchedPayoutSummary({
            totalPayouts: data.total_payout,
            totalPaid: data.total_paid,
            lastPayoutOn: data.last_payout,
            minPayout: data.min_payout_amount,
            maxPayout: data.max_payout_amount,
          });
        } catch (error) {
          console.error('Failed to fetch payout manual info:', error);
          setFetchedPayoutSummary(null);
        }
      } else {
        setFetchedPayoutSummary(null);
      }
    };

    fetchPayoutSummary();
  }, [open, account?.id]);

  const userInfoParts = [
    `${user?.first_name ?? ''} ${user?.last_name ?? ''}`,
    user?.user.email ?? '',
  ].filter(Boolean);

  const userInfo = userInfoParts.join(', ');

  const balanceDateFormatted = account?.account_balance_date
    ? formatDate(account.account_balance_date)
    : '';

  // Use fetched data if available, otherwise fall back to prop
  const summaryData = fetchedPayoutSummary || payoutSummary;

  const totalPayoutsDisplay =
    typeof summaryData?.totalPayouts === 'number'
      ? String(summaryData?.totalPayouts)
      : '-';
  const totalPaidDisplay = (() => {
    if (typeof summaryData?.totalPaid === 'number') {
      return `$${formatCurrency(Number(summaryData.totalPaid), 2)}`;
    }
    if (typeof summaryData?.totalPaid === 'string') {
      const n = Number(summaryData.totalPaid);

      return isNaN(n) ? summaryData.totalPaid : `$${formatCurrency(n, 2)}`;
    }

    return '-';
  })();
  const lastPayoutDisplay = summaryData?.lastPayoutOn
    ? formatDate(summaryData.lastPayoutOn)
    : '-';

  const minPayoutDisplay = summaryData?.minPayout
    ? `$${formatCurrency(Number(summaryData.minPayout), 2)}`
    : '$250';
  const maxPayoutDisplay = summaryData?.maxPayout
    ? `$${formatCurrency(Number(summaryData.maxPayout), 2)}`
    : '$1,200';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size={'xl'}>
        <DialogHeader>
          <DialogTitle className="text-2xl">{`Add Payout Request`}</DialogTitle>
          <DialogDescription
            className={'text-white text-xs'}
          >{`You are about to create a PA payout request.`}</DialogDescription>
        </DialogHeader>
        <form autoComplete="off" onSubmit={handleSubmit(onSave)}>
          <div className={'text-xs'}>
            <p className={'font-bold'}>User: {userInfo}</p>
            <p className={'my-2'}>
              <strong>{account?.rithmic_account_name ?? ''}</strong>{' '}
              {account?.product?.title ?? ''}
            </p>
            <p>
              <strong>Balance</strong>: $
              {formatCurrency(Number(account?.account_balance), 2)}
            </p>
            <p className={'mt-0.5 text-[11px] text-white/80'}>
              {balanceDateFormatted}
            </p>
            {fetchedPayoutSummary?.totalPayouts &&
              fetchedPayoutSummary?.totalPayouts > 0 && (
                <div className={'mt-4 flex flex-col gap-1'}>
                  <p>
                    <strong>Total Payouts:</strong> {totalPayoutsDisplay}
                  </p>
                  <p>
                    <strong>Total Paid:</strong> {totalPaidDisplay}
                  </p>
                  <p>
                    <strong>Last Payout:</strong> {lastPayoutDisplay}
                  </p>
                </div>
              )}
            <Divider className={'my-4'} />
            <p>
              Choose an amount from {minPayoutDisplay} to {maxPayoutDisplay}. Or
              you can choose any amount and override the payout logic.
            </p>
            <div className={'flex mt-4 gap-5'}>
              <Controller
                control={control}
                name="rithmic_deduction_amount"
                render={({ field }) => (
                  <div className={'flex-1'}>
                    <Label
                      className="text-xs mb-2"
                      htmlFor="rithmic_deduction_amount"
                    >
                      Rithmic Deduction Amount $
                    </Label>
                    <Input
                      {...field}
                      id="rithmic_deduction_amount"
                      placeholder={'Enter'}
                      textSize={'xs'}
                      type={'number'}
                      variant={'secondary'}
                    />
                    <p className={'mt-1'}>
                      Will default to payout amount unless you add another value
                    </p>
                    {errors.rithmic_deduction_amount && (
                      <p className="mt-1 text-red-500">
                        {errors.rithmic_deduction_amount.message}
                      </p>
                    )}
                  </div>
                )}
              />
              <Controller
                control={control}
                name="payout_amount"
                render={({ field }) => (
                  <div className={'flex-1'}>
                    <Label className="text-xs mb-2" htmlFor="payout_amount">
                      Payout Amount $
                    </Label>
                    <Input
                      {...field}
                      id="payout_amount"
                      placeholder={'Enter'}
                      textSize={'xs'}
                      type={'number'}
                      variant={'secondary'}
                    />
                    <p className={'mt-1'}>
                      Please ensure you choose the correct amount!
                    </p>
                    {errors.payout_amount && (
                      <p className="mt-1 text-red-500">
                        {errors.payout_amount.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
            <Controller
              control={control}
              name="override"
              render={({ field }) => (
                <div className={'my-4'}>
                  <Checkbox
                    checked={field.value}
                    id={`override`}
                    label={'Override payout logic and rules?'}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                  />
                </div>
              )}
            />
            <div className={'mb-4'}>
              <Label className={'text-xs mb-2'} htmlFor="payout_amount">
                Payment Method(s)
              </Label>
              <Controller
                control={control}
                name="payment_methods"
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onValueChange={(v) => field.onChange(v)}
                  >
                    <div className={'flex gap-2'}>
                      <RadioGroupItem value={'riseworks'} />
                      <p>{"Riseworks ID '0x57..954'"}</p>
                    </div>
                  </RadioGroup>
                )}
              />
            </div>
            <Controller
              control={control}
              name="note"
              render={({ field }) => (
                <div>
                  <Label className="text-xs mb-2" htmlFor="note">
                    Note
                  </Label>
                  <Textarea
                    {...field}
                    hiddenHeader
                    className={'h-20'}
                    id="note"
                    placeholder={'Optional'}
                  />
                </div>
              )}
            />
          </div>
          <div className="flex items-center gap-4 mt-10">
            <Button size={'sm'} type={'submit'} variant={'lime'}>
              Save
            </Button>
            <Button
              className={'w-20'}
              size={'sm'}
              onClick={() => {
                onOpenChange(false);
              }}
            >
              CLose
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
